import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios, { AxiosError } from 'axios';
import { useFormikContext } from 'formik';
import * as Sentry from '@sentry/browser';

import { useAppContext, useBeforeUnload } from '../../hooks';
import {
  getAddressComponentNames,
  mapGoogleHoursToCustomFormat,
} from '../../utils';

import FormField from '../FormField';
import FormButton from '../FormButton';

import StepHeading from './StepHeading';
import PhoneFormField from './PhoneFormField';
import NameFormField from './NameFormField';
import AddressFormField from './AddressFormField';
import AddressConfirmation from './AddressConfirmation';

function getUserPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        resolve({ latitude, longitude });
      },
      (error) => {
        reject(error);
      }
    );
  });
}

function SignupFormStep2({ onNextStep }) {
  const { placesService, validationService } = useAppContext();
  const [countryCode, setCountryCode] = useState(null);
  const [showAddressConfirmation, setShowAddressConfirmation] = useState(false);
  const { values, errors, dirty, setValues, setFieldError, setFieldTouched } =
    useFormikContext();

  useBeforeUnload(dirty);

  const disableNext = !!(
    errors.facility_name ||
    errors.google_places_id ||
    errors.phone ||
    errors.website
  );

  const handlePlaceSelect = async (place) => {
    const {
      route,
      street_number,
      locality,
      postal_town,
      sublocality_level_1,
      country,
      administrative_area_level_1,
      postal_code,
    } = getAddressComponentNames(place.address_components, [
      'route',
      'street_number',
      'locality',
      'postal_town',
      'sublocality_level_1',
      ['country', true],
      'administrative_area_level_1',
      'postal_code',
    ]);

    setValues((values) => ({
      ...values,
      city: locality || postal_town || sublocality_level_1,
      country: country,
      state: administrative_area_level_1,
      street: `${route} ${street_number}`.trim(),
      zip_code: postal_code,
      google_places_id: place.place_id,
    }));

    const details = await placesService.getDetails({
      placeId: place.place_id,
      fields: [
        'opening_hours',
        'secondary_opening_hours',
        'takeout',
        'delivery',
        'website',
        'formatted_phone_number',
        'international_phone_number',
      ],
    });

    const convertedOpeningHours = mapGoogleHoursToCustomFormat(
      details.opening_hours?.periods
    );

    const convertedBookingHours = mapGoogleHoursToCustomFormat(
      details.secondary_opening_hours?.find((sh) => sh.type === 'KITCHEN')
        ?.periods
    );

    setValues((values) => ({
      ...values,
      ...(details.international_phone_number
        ? { phone: details.international_phone_number.replace(/\s/g, '') }
        : details.formatted_phone_number
          ? { phone: details.formatted_phone_number.replace(/\s/g, '') }
          : {}),
      ...(details.website ? { website: details.website } : {}),
      visual_opening_hours: convertedOpeningHours,
      opening_hours: convertedBookingHours,
    }));

    setTimeout(() => {
      if (
        details.international_phone_number ||
        details.formatted_phone_number
      ) {
        setFieldError('phone', false);
        setFieldTouched('phone', false, false);
      }
    }, 300);
  };

  const handlePlaceDismiss = () => {
    setValues((values) => ({
      ...values,
      city: '',
      country: '',
      state: '',
      street: '',
      zip_code: '',
      facility_name: '',
      google_places_id: '',
    }));
    setShowAddressConfirmation(false);
  };

  const handleNextStep = async () => {
    try {
      await validationService.validate(values.google_places_id);
      onNextStep();
    } catch (e) {
      if (e instanceof AxiosError) {
        const { response } = e;
        if (response.status === 400) {
          const error = response.data.errors[0];
          const message = `${error.title} ${error.detail}`;
          setFieldError('google_places_id', message);
          return;
        }
      }
      Sentry.captureException(e);
      console.log(e);
    }
  };

  useEffect(() => {
    getUserPosition()
      .then(({ latitude, longitude }) =>
        placesService.getUsersCountryCode(latitude, longitude)
      )
      .then((countryCode) => {
        setCountryCode(countryCode);
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.log(error);
      });
  }, []); // eslint-disable-line

  return (
    <Fragment>
      <StepHeading style={{ marginBottom: '3rem' }}>
        {i18n.gettext('Tell us about your restaurant')}
      </StepHeading>
      <NameFormField
        countryCode={countryCode}
        onPlaceSelect={(place) => {
          handlePlaceSelect(place);
          setShowAddressConfirmation(true);
        }}
        onPlaceDismiss={
          showAddressConfirmation ? handlePlaceDismiss : undefined
        }
      />
      {showAddressConfirmation ? (
        <AddressConfirmation onPlaceDismiss={handlePlaceDismiss} />
      ) : (
        <AddressFormField
          countryCode={countryCode}
          onPlaceSelect={handlePlaceSelect}
          onPlaceDismiss={handlePlaceDismiss}
        />
      )}
      <PhoneFormField
        name="phone"
        label={i18n.gettext('Restaurant phone')}
        hint={i18n.gettext('As contact information for your guests')}
        required={false}
      />
      <FormField
        name="website"
        label={i18n.gettext('Restaurant website')}
        required={false}
      />
      <FormButton
        type="button"
        label={i18n.gettext('Next')}
        disabled={disableNext}
        style={{ marginTop: '2rem' }}
        onClick={handleNextStep}
      />
    </Fragment>
  );
}

SignupFormStep2.propTypes = {
  onNextStep: PropTypes.func,
};

export default SignupFormStep2;
